import Moment from 'moment-timezone';
import { cloneDeep, uniqBy } from 'lodash';
import { i18n } from '@/plugins/i18n';
import {
  GET_TEAMS,
  GET_INDICATOR_THEMES,
  GET_CFG_PAGES,
  GET_ANNOUNCEMENTS,
  GET_ZONES,
  GET_PB_TYPES,
  GET_USERS,
} from '@/store/mutation-types';
import { GET_NAVIGATION_LIST } from '@/store/modules/navigation/mutation-types';
import { nestedFlatten } from '@/utils/main';

/** Default presets for periods display config in indicators pages */
export const INDICATOR_PERIOD_PRESETS = [
  {
    id: 0,
    labelTr: 'message.DAILY_INDICATORS_DAY_VALUE',
    pointPeriodObj: { period: 1, unit: 'd', startDay: 1 },
    chartPeriodObj: { period: 1, unit: 'd' },
    chartEndDate: null,
  },
  {
    id: 4,
    labelTr: 'message.DAILY_INDICATORS_YESTERDAY_VALUE',
    pointPeriodObj: { period: 1, unit: 'd', startDay: 1 },
    chartPeriodObj: { period: 1, unit: 'd' },
    chartEndDate: null,
    dateGapFromCurrent: 1,
    dateGapUnit: 'd',
  },
  {
    id: 1,
    labelTr: 'message.DAILY_INDICATORS',
    pointPeriodObj: { period: 1, unit: 'd', startDay: 1 },
    chartPeriodObj: { period: 10, unit: 'd' },
    chartEndDate: null,
  },
  {
    id: 5,
    labelTr: 'message.DAILY_INDICATORS_OVER_THIRTY_DAYS',
    pointPeriodObj: { period: 1, unit: 'd', startDay: 1 },
    chartPeriodObj: { period: 1, unit: 'M' },
    chartEndDate: null,
  },
  {
    id: 2,
    labelTr: 'message.WEEKLY_INDICATORS',
    pointPeriodObj: { period: 1, unit: 'w', startDay: 1 },
    chartPeriodObj: { period: 5, unit: 'w' },
    chartEndDate: null,
  },
  {
    id: 3,
    labelTr: 'message.MONTHLY_INDICATORS',
    pointPeriodObj: { period: 1, unit: 'M', startDay: 1 },
    chartPeriodObj: { period: 5, unit: 'M' },
    chartEndDate: null,
  },
  // WARNING: ID 6 is taken for the wheel view, and cannot be used as a default config.
];

export const INDICATOR_WHEEL_VIEW_PRESET = {
  id: 6,
  labelTr: 'message.WIDGET_NAME_WHEEL',
  pointPeriodObj: { period: 1, unit: 'M', startDay: 1 },
  chartPeriodObj: { period: 1, unit: 'M' },
  chartEndDate: null,
};

/**
 * Create IndicatorDisplayConfig from an array of objects
 * @param  {Array} configs  Array of configs
 * @return {Array}          Array of IndicatorDisplayConfig
 */
function makeDisplayConfigs(configs) {
  const defaultParams = {
    increasingValues: 0,
    increasingObjectives: 0,
    increasePeriod: 1,
    increasePeriodUnit: 'M',
    chartEndDate: null,
    dateGapFromCurrent: null,
    dateGapUnit: 'd',
    chartEnd: Moment(),
  };
  return configs.map(item => ({ ...defaultParams, ...item, name: i18n.t(item.labelTr) }));
}

/**
 * Format indicator display configs to make them usable in the display config edition
 * @param  {Array} configs  Array of configs
 * @return {Array}          Array of formatted indicator display configs
 */
function makeFormattedDisplayConfigs(configs) {
  return configs.map(item => ({
    ...item,
    pointPeriod: item.pointPeriodObj.period,
    pointUnit: item.pointPeriodObj.unit,
    pointStartDay: item.pointPeriodObj.startDay,
    chartPeriod: item.chartPeriodObj.period,
    chartUnit: item.chartPeriodObj.unit,
  }));
}


// Speed up the children teams loading.
// Eg: Team 1 > Team a > Teams a1, a2, a3
//     Team 2 > Team a > Teams already loaded from previous subtree
let teamTreeProcessedTeamsPerId = {};
// Set all the children at any tree level for every team
// Eg: Team A is parent of Team A-a which is parent of team A-a-1 and A-a-2
// Then children['<teamA_ID'] = [<teamAa_ID>, <teamAa1_ID>, <teamAa2_ID>]
// Then children['<teamAa_ID'] = [<teamAa1_ID>, <teamAa2_ID>]
let childrenPerId = {};

/**
 *  Set its children to a specific team from the original array of teams
 *  Leave logs in place unless the hierarchy system changes
 * @param {Array} teams - source array of team objects
 * @param {Object} team - team object to add children to
 * @param {Array} parents - array of IDs which are parents of @param team ordered by closest
 */
function setChildren(teams, team, parents = []) {
  // parentId is used to filter out teams that have a parent
  team.parentId = parents.length ? parents[0] : 0;
  team.parents = parents;
  // If children teams already processed, set the value
  if (teamTreeProcessedTeamsPerId[`${team.id}`]) {
    team.teams = teamTreeProcessedTeamsPerId[`${team.id}`];
    team.children = childrenPerId[`${team.id}`];
    return [team.id, ...(team.children || [])];
  }
  team.teams = cloneDeep(teams.filter(t => team.supervisedTeamIds.includes(t.id) && !parents.includes(t.id)));
  // Children may have been spliced before setChildren was executed on them
  for (let i = 0, len = team.teams.length; i < len; i++) {
    const iChildren = setChildren(teams, team.teams[i], [team.id, ...parents]);
    team.children = [...(team.children || []), ...iChildren];
  }
  // Set as a re-usable array of children
  teamTreeProcessedTeamsPerId[`${team.id}`] = team.teams;
  childrenPerId[`${team.id}`] = team.children;
  return [team.id, ...(team.children || [])];
}

function createTeamsTree(teams, specificTeamId = 0) {
  teamTreeProcessedTeamsPerId = {};
  childrenPerId = {};
  // Root teams = teams that are not supervised by anyone OR specificTeamId
  const specTeam = teams.find(team => team.id === specificTeamId);
  const rootTeams = specTeam
    ? [specTeam]
    : teams.filter(team => !teams.some(t => t.supervisedTeamIds.includes(team.id)));
  // Set their children in the tree
  rootTeams.forEach((team) => {
    setChildren(teams, team);
  });
  return rootTeams;
}

export default {
  /** String: Current version of Shizen */
  version() {
    const version = process.env.VUE_APP_VERSION;
    return version && /^\d+\.\d+\.\d+$/.test(version)
      ? version
      : '';
  },

  /** Number: Font size from vars.scss */
  fontSize(state) {
    return state.device.isSmartphoneLandscape || state.device.isSmartphonePortrait ? 12 : 14;
  },

  /** Object: Basically CSS rules as JS object */
  pageProperties(state) {
    if (state.device.isSmartphoneLandscape || state.device.isSmartphonePortrait) {
      return {
        fontSize: 12,
        headerHeight: 40,
        navWidth: 40,
      };
    }
    return {
      fontSize: 14,
      headerHeight: 65,
      navWidth: 70,
    };
  },

  /** Array: Indicators from current team's themes */
  themeIndicators: (state) => {
    const themes = state[GET_INDICATOR_THEMES.stateKey] || [];
    return themes.flatMap(theme => theme.indicators);
  },

  /** Object: Users as { <user_id>: <user_object> } */
  companyUsers(state) {
    const teamsData = state[GET_TEAMS.stateKey] || [];
    const users = {};
    teamsData.forEach((team) => {
      team.users.forEach((user) => {
        users[user.id] = user;
      });
    });
    return users;
  },

  companyTeams(state) {
    const teamsData = state[GET_TEAMS.stateKey] || [];
    const teams = {};
    teamsData.forEach((team) => {
      teams[`${team.id}`] = team;
    });
    return teams;
  },

  /** Object: object where key is the team id and value is an array of users */
  usersPerTeam(state) {
    const teams = {};
    (state[GET_TEAMS.stateKey] || []).forEach((team) => {
      teams[`${team.id}`] = team.users;
    });
    teams[`${state.currentTeam.id}`] = state[GET_USERS.stateKey] || [];
    return teams;
  },

  /**
   * Turn the zones tree into a key-value object where the key is the id of the zone
   * and the value is the zone object
   */
  zonesPerId(state) {
    const allZones = nestedFlatten(state[GET_ZONES.stateKey] || [], 'zones');
    const zones = {};
    for (let i = 0, len = allZones.length, zone; i < len; i++) {
      zone = allZones[i];
      zones[`${zone.id}`] = zone;
    }
    return zones;
  },

  /**
   * Turn the pbTypes tree into a key-value object where the key is the id of the type
   * and the value is the type object
   */
  pbTypesPerId(state) {
    const allTypes = nestedFlatten(state[GET_PB_TYPES.stateKey] || [], 'types');
    const types = {};
    for (let i = 0, len = allTypes.length, type; i < len; i++) {
      type = allTypes[i];
      types[`${type.id}`] = type;
    }
    return types;
  },

  getCfgPageFromId: state => (id) => {
    const cfgPages = state[GET_CFG_PAGES.stateKey] || [];
    return cfgPages.find(p => p.id === id);
  },

  displayedAnnouncements(state) {
    const announcements = state[GET_ANNOUNCEMENTS.stateKey] || [];
    return announcements.filter(item => !state.hiddenAnnouncements.includes(item.id));
  },

  nHiddenAnnouncements(state, getters) {
    const announcements = state[GET_ANNOUNCEMENTS.stateKey] || [];
    return announcements.length - getters.displayedAnnouncements.length;
  },

  /** Turn an array of team objects into a hierarchy tree where the root is the current team */
  teamsTreeFromCurrent(state, getters) {
    return state.currentTeam ? getters.teamsHierarchyTree(state.currentTeam.id) : [];
  },

  /** Turn an array of team objects into a hierarchy tree where the root are the top management teams */
  allTeamsTree(state, getters) {
    return getters.teamsHierarchyTree(0);
  },

  /** teamsTreeFromCurrent as a flattened array */
  teamsTreeCurrentFlattened(state, getters) {
    return nestedFlatten(getters.teamsTreeFromCurrent, 'teams');
  },

  /** allTeamsTree as a flattened array */
  allTeamsTreeFlattened(state, getters) {
    return nestedFlatten(getters.allTeamsTree, 'teams');
  },

  /** Object: allTeamsTree as an object in the following format: { <team_id>: <team_object>, ... }  */
  allTeamsPerId(state, getters) {
    return getters.allTeamsTreeFlattened.reduce((all, team) => ({ ...all, [`${team.id}`]: team }), {});
  },

  /** allTeamsTree as a flattened array */
  allAccessibleTeams(state, getters) {
    const { id, users = [] } = state.currentTeam;
    const userIds = users.map(u => u.id);
    const accessibleTeams = getters.allTeamsTreeFlattened.filter((team) => {
      const isChild = team.parents.includes(id);
      const isAccessible = team.accesses.some(uId => userIds.includes(uId));
      return team.id !== id && (isChild || isAccessible);
    });
    return uniqBy(accessibleTeams, 'id');
  },

  /**
   * Turn an array of team objects into a hierarchy tree
   * @param  {Number} specificTeamId if set and > 0, return the element with given id as the tree root
   * @return {Array} tree-like teams array
   */
  teamsHierarchyTree: state => (specificTeamId = 0) => {
    const tree = createTeamsTree(cloneDeep(state[GET_TEAMS.stateKey] || []), specificTeamId);
    return tree;
  },

  /**
   * Default display configs for this team
   * @return {Array}       Array of IndicatorDisplayConfig objects
   */
  indicDisplayConfigs() {
    return makeDisplayConfigs(INDICATOR_PERIOD_PRESETS);
  },

  /**
   * Default display configs for this team. Formatted to be editable in the config editon.
   * @return {Array}       Array of IndicatorDisplayConfig objects
   */
  formIndicDisplayConfigs(state, getters) {
    return makeFormattedDisplayConfigs(getters.indicDisplayConfigs);
  },

  /**
   * Display config used to display indicator wheels. Shouldn't be used in any other scenario
   * as this simply is a p=1M c=1M config.
   * @return {Object}       Diplay config used for the Wheel view
   */
  wheelDisplayConfig() {
    return makeDisplayConfigs([INDICATOR_WHEEL_VIEW_PRESET])[0];
  },
  formWheelDisplayConfig() {
    return makeFormattedDisplayConfigs(makeDisplayConfigs([INDICATOR_WHEEL_VIEW_PRESET]))[0];
  },

  /**
   * Indicator display config selected by default
   * @return {Object} IndicatorDisplayConfig object
   */
  defaultIndicDisplayConfig(state, getters) {
    return { ...getters.indicDisplayConfigs.find(config => config.id === 1), chartEnd: Moment() };
  },
  defaultMonthlyDisplayConfig(state, getters) {
    return { ...getters.indicDisplayConfigs.find(({ id }) => id === 3), chartEnd: Moment() };
  },

  /**
   * Json web token needed to auth
   * @return {String} state.jwt
   */
  currentJwt(state) {
    return state.jwt || '';
  },

  /**
   * Determine if we have to fetch number of pending actions
   * @return {Boolean} Whether the navigation items need refreshing the number of pending actions
   */
  needPendingActionsNumber(state) {
    const navItems = state.navigation[GET_NAVIGATION_LIST.stateKey] || [];
    return navItems.some(item => item.pageOptions && item.pageOptions.displayNumberOfPendingActions);
  },

  /**
   * Size of a PDCA canvas
   * @return {Number} size in pixels
   */
  pdcaSize() {
    return 50;
  },
};
